import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SalesActions } from "@/components/crm/SalesActions";
import { OpportunityDialog } from "@/components/crm/OpportunityDialog";
import { RecordDrawer } from "@/components/crm/RecordDrawer";
import { SalesShell } from "@/components/crm/SalesShell";
import { BulkBar, SelectBox, useRowSelection } from "@/components/crm/BulkBar";
import { EmptyState, SelectField } from "@/components/crm/parts";
import { suggestionsFor } from "@/lib/crm/suggestions";
import { pipelineSheets } from "@/lib/exports/crm";
import { opportunityVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import {
  useAccounts,
  useContracts,
  useDeleteRecord,
  useOpportunities,
  useStages,
} from "@/lib/crm/useCrm";
import {
  FORECAST_CATEGORIES,
  gbp,
  isOpen,
  labelOf,
  probabilityOf,
  weightedValue,
  type Opportunity,
} from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/opportunities")({
  head: () => ({
    meta: [
      { title: "Opportunities — MyPriorities" },
      {
        name: "description",
        content:
          "Every open and closed opportunity with owner, stage, weighted value, forecast category and next action.",
      },
      { property: "og:title", content: "Opportunities — MyPriorities" },
      {
        property: "og:description",
        content: "Filter, forecast and export your opportunity list.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OpportunitiesPage,
});

function OpportunitiesPage() {
  return (
    <SalesShell title="Opportunities" subtitle="Owner, stage, weighted value and the next action.">
      {({ orgId, userId }) => <List orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

function List({ orgId, userId }: { orgId: string; userId: string }) {
  const stages = useStages(orgId);
  const opportunities = useOpportunities(orgId);
  const accounts = useAccounts(orgId);
  const contracts = useContracts(orgId);
  const { ownerLabel, ownerOptions } = useOrgMembers(orgId);
  const remove = useDeleteRecord("crm_opportunities");
  const selection = useRowSelection();

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [openOnly, setOpenOnly] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [detail, setDetail] = useState<Opportunity | null>(null);

  const { ownerId: ownerFilter } = useSalesOwner();
  const stageRows = stages.data ?? [];
  const accountRows = accounts.data ?? [];
  const accountName = (id: string | null) =>
    accountRows.find((a) => a.id === id)?.name ?? "No account";

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (opportunities.data ?? []).filter((o) => {
      if (openOnly && !isOpen(o, stageRows)) return false;
      if (stageFilter !== "all" && o.stage_id !== stageFilter) return false;
      if (ownerFilter !== "all" && o.owner_user_id !== ownerFilter) return false;
      if (!q) return true;
      return (
        o.name.toLowerCase().includes(q) || accountName(o.account_id).toLowerCase().includes(q)
      );
    });
  }, [opportunities.data, search, stageFilter, ownerFilter, openOnly, stageRows, accountRows]);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-full sm:basis-56">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search opportunities or accounts"
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-40">
          <SelectField
            label="Stage"
            value={stageFilter}
            onChange={setStageFilter}
            options={[
              { value: "all", label: "All stages" },
              ...stageRows.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </div>
        <Button
          className="shrink-0"
          variant={openOnly ? "default" : "outline"}
          onClick={() => setOpenOnly((v) => !v)}
        >
          {openOnly ? "Open only" : "All deals"}
        </Button>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
          <SalesActions
            visual={() => opportunityVisual(rows, stageRows, accountName)}
            sourceNote="From your opportunity list"
            exports={[
              {
                label: "Opportunities",
                baseName: "opportunities",
                sheets: () =>
                  pipelineSheets({
                    opportunities: rows,
                    accounts: accountRows,
                    stages: stageRows,
                    ownerLabel,
                  }),
              },
            ]}
          />
          <Button
            className="shrink-0"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> New
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No opportunities here"
          body="Create one, or loosen the filters to see closed deals."
        />
      ) : (
        <>
        <BulkBar
          table="crm_opportunities"
          noun="opportunity"
          ids={rows.map((r) => r.id)}
          selected={selection.selected}
          onReplace={selection.replace}
          onClear={selection.clear}
          blockedHint="They are linked to contracts or approvals."
        />
        <ul className="grid gap-2">
          {rows.map((o) => (
            <li key={o.id} className="rounded-lg border border-border bg-card p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <SelectBox
                    checked={selection.isSelected(o.id)}
                    onChange={() => selection.toggle(o.id)}
                    label={`Select ${o.name}`}
                  />
                <div className="min-w-0">
                  <button
                    type="button"
                    className="text-left text-sm font-semibold hover:underline"
                    onClick={() => setDetail(o)}
                  >
                    {o.name}
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    {accountName(o.account_id)} · {ownerLabel(o.owner_user_id)} ·{" "}
                    {stageRows.find((s) => s.id === o.stage_id)?.name ?? "No stage"}
                  </p>
                  {o.next_action ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Next: {o.next_action}
                      {o.next_action_date ? ` (${o.next_action_date})` : ""}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground">No next action set</p>
                  )}
                </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{gbp(o.expected_value)} gross</Badge>
                  <Badge variant="secondary">
                    {gbp(weightedValue(o, stageRows))} @ {probabilityOf(o, stageRows)}%
                  </Badge>
                  <Badge variant="outline">
                    {labelOf(FORECAST_CATEGORIES, o.forecast_category)}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(o);
                      setDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete opportunity"
                    onClick={() => {
                      void remove
                        .mutateAsync(o.id)
                        .then(() => toast.success("Opportunity deleted"))
                        .catch((e: unknown) =>
                          toast.error(e instanceof Error ? e.message : "Could not delete"),
                        );
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      <OpportunityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={orgId}
        userId={userId}
        opportunity={editing}
        accounts={accountRows}
        contracts={contracts.data ?? []}
        stages={stageRows}
        ownerOptions={ownerOptions}
      />

      <RecordDrawer
        open={Boolean(detail)}
        onOpenChange={(v) => !v && setDetail(null)}
        orgId={orgId}
        userId={userId}
        kind="opportunity"
        recordId={detail?.id ?? null}
        title={detail?.name ?? ""}
        subtitle={detail ? accountName(detail.account_id) : undefined}
        suggestions={detail ? suggestionsFor(detail, stageRows) : []}
      />
    </div>
  );
}
